import get from 'lodash/get';
import set from 'lodash/set';
import startCase from 'lodash/startCase';

import {
  ICoverageValues,
  TCoverageSideEffectsType,
  TCoverageUpdateType,
  ECoverageVariableKey,
  ERegionCode,
  IVehicleCoverageValues,
  IVehicle,
  IPolicyVehicle,
  IAutoVariables,
} from '../../interfaces';
import calculateClosestVariableOptionEqualOrBelow from '../../services/calculateClosestVariableOption';
import coverageOptionStrToNum from '../../services/coverageOptionStrToNum';
import { REGION_COVERAGE_RULES } from '../constants/regionConfigs';

import {
  TCoverageValues,
  vehicleCoverageOnChangeWrapper,
} from './vehicleCoverageOnChangeWrapper';

const whitespaceRegex = /\s/g;

const coverageUpdateMap: Record<TCoverageUpdateType, string> = {
  COMPREHENSIVE_COLLISION_COVERAGE_UPDATE: 'Comprehensive Collision',
  BODILY_INJURY_AND_UMBI_UPDATE:
    'Bodily Injury  and Uninsured Motorist Bodily Injury',
  BODILY_INJURY_UPDATE: 'Bodily Injury',
  PROPERTY_DAMAGE_UPDATE: 'Property Damage',
  UPDATE_COVERAGES: 'Update Coverages',
  UIMBI_UPDATE: 'Underinsured Motorist Bodily Injury',
  UMBI_UPDATE: 'Uninsured Motorist Bodily Injury',
  UMBI_AND_UIMBI_UPDATE:
    'Uninsured Motorist Bodily Injury and Underinsured Motorist Bodily Injury',
  UMPD_UPDATE: 'Uninsured Motorist Property Damage',
  UMPD_DEDUCTIBLE_UPDATE: 'Uninsured Motorist Property Damage Deductible',
  UIMPD_UPDATE: 'Underinsured Motorist Property Damage',
  PROPERTY_DAMAGE_AND_UMPD_UPDATE:
    'Property Damage and Uninsured Motorist Property Damage',
  VEHICLE_LEVEL_COVERAGES_UPDATE: 'Vehicle Level Coverages',
  VEHICLE_UMPD_UPDATE: 'Uninsured Motorist Property Damage',
  VEHICLE_ADD_ONS_REMOVED: 'dependent',
  OEM_ADDON_ENABLED: 'Original Parts Replacement',
  ADDON_ENABLED: 'addon',
  EXTRAORDINARY_MEDICAL_UPDATE: 'Extraordinary Medical Benefit',
  UMST_UPDATE: 'Uninsured Motorist Bodily Injury Stacking Option',
  UIMST_UPDATE: 'Underinsured Motorist Bodily Injury Stacking Option',
};

function formatCauseName(cause: ECoverageVariableKey) {
  return startCase(cause.replace('_limit', ''));
}

const {
  HIDE_UMBI_IF_UMPD_OFF,
  PD_CANNOT_EXCEED_BI,
  UIMBI_CANNOT_EXCEED_UMBI,
  UMBI_MATCHES_UIMBI,
  UMBI_UIMBI_COVERAGE_MAP,
  UMPD_REQUIRES_UMBI,
  SKIP_UIMBI_SIDE_EFFECT,
  CDW_REQUIRES_UMBI_AND_COLL,
  SKIP_UMPD_SIDE_EFFECT,
  UMBI_UIMBI_EXCEPTIONS,
  HIDE_UMST_IF_UMBI_NOT_INCLUDED,
  HIDE_UIMST_IF_UIMBI_NOT_INCLUDED,
} = REGION_COVERAGE_RULES;

type TCoverageUpdateSideEffects = {
  values: ICoverageValues;
  currentlyTargetedKeyName: ECoverageVariableKey;
  regionCode?: ERegionCode;
  vehicles?: (IVehicle | IPolicyVehicle)[];
  enabledSideEffects?: string[];
  policyEffectiveDate: string;
  coverageVariableOptions: IAutoVariables;
};

export function coverageUpdateSideEffects({
  values,
  currentlyTargetedKeyName,
  regionCode = 'SC',
  vehicles = [],
  enabledSideEffects = [],
  policyEffectiveDate,
  coverageVariableOptions,
}: TCoverageUpdateSideEffects) {
  const typesOfUpdates: TCoverageUpdateType[] = [];
  const sideEffectContext: TCoverageSideEffectsType[] = [];
  const updatedValues = values;

  const regionSpecificUMBIUIMBICoverageMap = UMBI_UIMBI_COVERAGE_MAP.find(
    ({ region }) => region === regionCode
  );

  const {
    bodily_injury_limit,
    property_damage_limit,
    underinsured_motorist_bodily_injury_limit,
    underinsured_motorist_property_damage_limit,
    underinsured_motorist_stacked,
    uninsured_motorist_bodily_injury_limit,
    uninsured_motorist_stacked,
    uninsured_motorist_property_damage_limit,
    uninsured_motorist_property_damage_deductible,
    vehicles: vehiclesValues,
  } = values;

  function getRegionSpecificUIMBICompareCondition(
    value?: string | null,
    regionSpecificValue?: string | null
  ) {
    return regionSpecificValue &&
      coverageOptionStrToNum(underinsured_motorist_bodily_injury_limit) !== 0
      ? regionSpecificValue !== underinsured_motorist_bodily_injury_limit
      : coverageOptionStrToNum(value) <
          coverageOptionStrToNum(underinsured_motorist_bodily_injury_limit);
  }

  const [sanitizedUmbiLimit, additionalCoverageString] =
    uninsured_motorist_bodily_injury_limit?.split('-', 2) || [];

  const regionExceptionsMap = UMBI_UIMBI_EXCEPTIONS.find(
    (regionExcpetionsMap) => regionExcpetionsMap.region === regionCode
  );
  const hasUimbiException = regionExceptionsMap?.exceptions.find(
    ({ umbi, uimbi }) =>
      umbi === uninsured_motorist_bodily_injury_limit &&
      uimbi === underinsured_motorist_bodily_injury_limit
  );

  switch (currentlyTargetedKeyName) {
    case 'property_damage_limit':
      if (uninsured_motorist_property_damage_limit) {
        if (!SKIP_UMPD_SIDE_EFFECT.includes(regionCode)) {
          if (
            coverageOptionStrToNum(property_damage_limit) <
            coverageOptionStrToNum(uninsured_motorist_property_damage_limit)
          ) {
            updatedValues.uninsured_motorist_property_damage_limit =
              property_damage_limit;
            sideEffectContext.push({
              type: 'UMPD_UPDATE',
              name: coverageUpdateMap.UMPD_UPDATE,
              isIncreased: false,
              cause: currentlyTargetedKeyName,
              causeName: formatCauseName(currentlyTargetedKeyName),
            });
          }
        }
      }
      if (underinsured_motorist_property_damage_limit) {
        if (
          coverageOptionStrToNum(property_damage_limit) <
          coverageOptionStrToNum(underinsured_motorist_property_damage_limit)
        ) {
          updatedValues.underinsured_motorist_property_damage_limit =
            property_damage_limit;
          sideEffectContext.push({
            type: 'UIMPD_UPDATE',
            name: coverageUpdateMap.UIMPD_UPDATE,
            isIncreased: false,
            cause: currentlyTargetedKeyName,
            causeName: formatCauseName(currentlyTargetedKeyName),
          });
        }
      }
      break;

    case 'uninsured_motorist_property_damage_limit':
      if (
        uninsured_motorist_property_damage_limit &&
        underinsured_motorist_property_damage_limit
      ) {
        if (!SKIP_UMPD_SIDE_EFFECT.includes(regionCode)) {
          if (
            coverageOptionStrToNum(uninsured_motorist_property_damage_limit) >
            coverageOptionStrToNum(property_damage_limit)
          ) {
            updatedValues.property_damage_limit =
              uninsured_motorist_property_damage_limit;
            sideEffectContext.push({
              type: 'PROPERTY_DAMAGE_UPDATE',
              name: coverageUpdateMap.PROPERTY_DAMAGE_UPDATE,
              isIncreased: true,
              cause: currentlyTargetedKeyName,
              causeName: formatCauseName(currentlyTargetedKeyName),
            });
          }
        }
        if (
          coverageOptionStrToNum(uninsured_motorist_property_damage_limit) <
          coverageOptionStrToNum(underinsured_motorist_property_damage_limit)
        ) {
          updatedValues.underinsured_motorist_property_damage_limit =
            uninsured_motorist_property_damage_limit;
          sideEffectContext.push({
            type: 'UIMPD_UPDATE',
            name: coverageUpdateMap.UIMPD_UPDATE,
            isIncreased: false,
            cause: currentlyTargetedKeyName,
            causeName: formatCauseName(currentlyTargetedKeyName),
          });
        }
      }
      if (HIDE_UMBI_IF_UMPD_OFF.includes(regionCode)) {
        const isUmpdIncluded =
          uninsured_motorist_property_damage_limit !== null &&
          uninsured_motorist_property_damage_limit !== 'null' &&
          uninsured_motorist_property_damage_limit !== undefined;
        const isUmbiIncluded =
          uninsured_motorist_bodily_injury_limit !== null &&
          uninsured_motorist_bodily_injury_limit !== 'null' &&
          uninsured_motorist_bodily_injury_limit !== undefined;
        if (!isUmpdIncluded) {
          updatedValues.uninsured_motorist_bodily_injury_limit = 'null';
          sideEffectContext.push({
            type: 'UMBI_UPDATE',
            name: coverageUpdateMap.UMBI_UPDATE,
            isRemoved: true,
            isIncreased: false,
            cause: currentlyTargetedKeyName,
            causeName: formatCauseName(currentlyTargetedKeyName),
          });
        }
        if (isUmpdIncluded && !isUmbiIncluded) {
          updatedValues.uninsured_motorist_bodily_injury_limit =
            coverageVariableOptions.uninsured_motorist_bodily_injury_limit.values.filter(
              (value) => value !== null
            )[0] as string;
          sideEffectContext.push({
            type: 'UMBI_UPDATE',
            name: coverageUpdateMap.UMBI_UPDATE,
            isRemoved: false,
            isIncreased: false,
            isAdded: true,
            cause: currentlyTargetedKeyName,
            causeName: formatCauseName(currentlyTargetedKeyName),
          });
        }
      }

      break;

    case 'underinsured_motorist_property_damage_limit':
      if (
        uninsured_motorist_property_damage_limit &&
        underinsured_motorist_property_damage_limit
      ) {
        if (
          !!coverageOptionStrToNum(
            underinsured_motorist_property_damage_limit
          ) !==
          !!coverageOptionStrToNum(underinsured_motorist_bodily_injury_limit)
        ) {
          const newValue =
            underinsured_motorist_bodily_injury_limit === 'null' ||
            underinsured_motorist_bodily_injury_limit === undefined
              ? '25000/50000'
              : 'null';

          updatedValues.underinsured_motorist_bodily_injury_limit = newValue;
          sideEffectContext.push({
            type: 'UIMBI_UPDATE',
            name: coverageUpdateMap.UIMBI_UPDATE,
            isIncreased: newValue !== 'null',
            isRemoved: newValue === 'null',
            cause: currentlyTargetedKeyName,
            causeName: formatCauseName(currentlyTargetedKeyName),
          });
        }

        if (
          coverageOptionStrToNum(underinsured_motorist_property_damage_limit) >
          coverageOptionStrToNum(property_damage_limit)
        ) {
          updatedValues.property_damage_limit =
            underinsured_motorist_property_damage_limit;
          sideEffectContext.push({
            type: 'PROPERTY_DAMAGE_UPDATE',
            name: coverageUpdateMap.PROPERTY_DAMAGE_UPDATE,
            isIncreased: true,
            cause: currentlyTargetedKeyName,
            causeName: formatCauseName(currentlyTargetedKeyName),
          });
        }

        if (
          coverageOptionStrToNum(underinsured_motorist_property_damage_limit) >
          coverageOptionStrToNum(uninsured_motorist_property_damage_limit)
        ) {
          updatedValues.uninsured_motorist_property_damage_limit =
            underinsured_motorist_property_damage_limit;
          sideEffectContext.push({
            type: 'UMPD_UPDATE',
            name: coverageUpdateMap.UMPD_UPDATE,
            isIncreased: true,
            cause: currentlyTargetedKeyName,
            causeName: formatCauseName(currentlyTargetedKeyName),
          });
        }
      }

      break;

    case 'bodily_injury_limit':
      const isNewBodilyInjuryLimitLessThanPropertyDamageLimit =
        coverageOptionStrToNum(bodily_injury_limit) <
        coverageOptionStrToNum(property_damage_limit);

      if (
        PD_CANNOT_EXCEED_BI.includes(regionCode) &&
        isNewBodilyInjuryLimitLessThanPropertyDamageLimit
      ) {
        updatedValues.property_damage_limit =
          calculateClosestVariableOptionEqualOrBelow(
            coverageOptionStrToNum(bodily_injury_limit),
            coverageVariableOptions.property_damage_limit.values as string[]
          )?.label;

        sideEffectContext.push({
          type: 'PROPERTY_DAMAGE_UPDATE',
          name: coverageUpdateMap.PROPERTY_DAMAGE_UPDATE,
          isIncreased: false,
          cause: currentlyTargetedKeyName,
          causeName: formatCauseName(currentlyTargetedKeyName),
        });
      }
      if (
        coverageOptionStrToNum(bodily_injury_limit) <
        coverageOptionStrToNum(sanitizedUmbiLimit)
      ) {
        if (additionalCoverageString) {
          updatedValues.uninsured_motorist_bodily_injury_limit = `${bodily_injury_limit} -${additionalCoverageString}`;
        } else {
          updatedValues.uninsured_motorist_bodily_injury_limit =
            bodily_injury_limit;
        }
        sideEffectContext.push({
          type: 'UMBI_UPDATE',
          name: coverageUpdateMap.UMBI_UPDATE,
          isIncreased: false,
          cause: currentlyTargetedKeyName,
          causeName: formatCauseName(currentlyTargetedKeyName),
        });
      }

      const regionSpecificCoverageValue =
        regionSpecificUMBIUIMBICoverageMap?.[bodily_injury_limit || ''];

      if (
        getRegionSpecificUIMBICompareCondition(
          bodily_injury_limit,
          regionSpecificCoverageValue
        )
      ) {
        updatedValues.underinsured_motorist_bodily_injury_limit =
          regionSpecificCoverageValue || bodily_injury_limit;
        sideEffectContext.push({
          type: 'UIMBI_UPDATE',
          name: coverageUpdateMap.UIMBI_UPDATE,
          isIncreased: false,
          cause: currentlyTargetedKeyName,
          causeName: formatCauseName(currentlyTargetedKeyName),
        });
      }
      break;

    case 'uninsured_motorist_bodily_injury_limit':
      const isUimbiGreaterThanUmbi =
        coverageOptionStrToNum(underinsured_motorist_bodily_injury_limit) >
        coverageOptionStrToNum(uninsured_motorist_bodily_injury_limit);
      const updatedUmbiLimit = sanitizedUmbiLimit.replace(whitespaceRegex, '');
      if (
        !hasUimbiException &&
        UIMBI_CANNOT_EXCEED_UMBI.includes(regionCode) &&
        isUimbiGreaterThanUmbi
      ) {
        updatedValues.underinsured_motorist_bodily_injury_limit =
          updatedUmbiLimit;
        sideEffectContext.push({
          type: 'UIMBI_UPDATE',
          name: coverageUpdateMap.UIMBI_UPDATE,
          isIncreased: false,
          cause: currentlyTargetedKeyName,
          causeName: formatCauseName(currentlyTargetedKeyName),
        });
      }
      if (
        coverageOptionStrToNum(uninsured_motorist_bodily_injury_limit) >
        coverageOptionStrToNum(bodily_injury_limit)
      ) {
        updatedValues.bodily_injury_limit = updatedUmbiLimit;
        sideEffectContext.push({
          type: 'BODILY_INJURY_UPDATE',
          name: coverageUpdateMap.BODILY_INJURY_UPDATE,
          isIncreased: true,
          cause: currentlyTargetedKeyName,
          causeName: formatCauseName(currentlyTargetedKeyName),
        });
      }

      if (
        CDW_REQUIRES_UMBI_AND_COLL.includes(regionCode) &&
        (uninsured_motorist_bodily_injury_limit === null ||
          uninsured_motorist_bodily_injury_limit === 'null') &&
        !!vehiclesValues
      ) {
        updatedValues.vehicles = vehiclesValues.map((vehicle) => {
          if (vehicle.values.collision_deductible_waiver) {
            sideEffectContext.push({
              type: 'VEHICLE_ADD_ONS_REMOVED',
              name: coverageUpdateMap.VEHICLE_ADD_ONS_REMOVED,
              isIncreased: false,
              isRemoved: true,
              cause: currentlyTargetedKeyName,
              causeName: formatCauseName(currentlyTargetedKeyName),
            });
            return {
              ...vehicle,
              values: {
                ...vehicle.values,
                collision_deductible_waiver: false,
              },
            } as IVehicleCoverageValues;
          }
          return vehicle;
        });
      }

      if (
        UMPD_REQUIRES_UMBI.includes(regionCode) &&
        (uninsured_motorist_bodily_injury_limit === null ||
          uninsured_motorist_bodily_injury_limit === 'null') &&
        uninsured_motorist_property_damage_limit &&
        uninsured_motorist_property_damage_limit !== 'null'
      ) {
        updatedValues.uninsured_motorist_property_damage_limit = 'null';
        sideEffectContext.push({
          type: 'UMPD_UPDATE',
          name: coverageUpdateMap.UMPD_UPDATE,
          isIncreased: false,
          isRemoved: true,
          cause: currentlyTargetedKeyName,
          causeName: formatCauseName(currentlyTargetedKeyName),
        });
      }

      if (
        UMPD_REQUIRES_UMBI.includes(regionCode) &&
        !!vehiclesValues &&
        (uninsured_motorist_bodily_injury_limit === null ||
          uninsured_motorist_bodily_injury_limit === 'null')
      ) {
        updatedValues.vehicles = vehiclesValues.map((vehicle) => {
          if (vehicle.values.uninsured_motorist_property_damage_limit) {
            sideEffectContext.push({
              type: 'VEHICLE_UMPD_UPDATE',
              name: coverageUpdateMap.UMPD_UPDATE,
              isIncreased: false,
              isRemoved: true,
              cause: currentlyTargetedKeyName,
              causeName: formatCauseName(currentlyTargetedKeyName),
            });
            return {
              ...vehicle,
              values: {
                ...vehicle.values,
                uninsured_motorist_property_damage_limit: null,
              },
            } as IVehicleCoverageValues;
          }
          return vehicle;
        });
      }

      if (!hasUimbiException && !SKIP_UIMBI_SIDE_EFFECT.includes(regionCode)) {
        const regionSpecificCoverageValue =
          regionSpecificUMBIUIMBICoverageMap?.[
            uninsured_motorist_bodily_injury_limit || ''
          ];

        if (UMBI_MATCHES_UIMBI.includes(regionCode)) {
          sideEffectContext.push({
            type: 'UIMBI_UPDATE',
            name: coverageUpdateMap.UIMBI_UPDATE,
            isIncreased:
              coverageOptionStrToNum(uninsured_motorist_bodily_injury_limit) >
              coverageOptionStrToNum(underinsured_motorist_bodily_injury_limit),
            cause: currentlyTargetedKeyName,
            causeName: formatCauseName(currentlyTargetedKeyName),
          });
          updatedValues.underinsured_motorist_bodily_injury_limit =
            regionSpecificCoverageValue ||
            uninsured_motorist_bodily_injury_limit;
        } else if (
          getRegionSpecificUIMBICompareCondition(
            uninsured_motorist_bodily_injury_limit,
            regionSpecificCoverageValue
          )
        ) {
          updatedValues.underinsured_motorist_bodily_injury_limit =
            regionSpecificCoverageValue ||
            uninsured_motorist_bodily_injury_limit;
          sideEffectContext.push({
            type: 'UIMBI_UPDATE',
            name: coverageUpdateMap.UIMBI_UPDATE,
            isIncreased: false,
            cause: currentlyTargetedKeyName,
            causeName: formatCauseName(currentlyTargetedKeyName),
          });
        }
      }

      if (HIDE_UMST_IF_UMBI_NOT_INCLUDED.includes(regionCode)) {
        const isNotIncluded = [null, 'null', undefined];
        if (
          isNotIncluded.includes(uninsured_motorist_bodily_injury_limit) &&
          uninsured_motorist_stacked
        ) {
          updatedValues.uninsured_motorist_stacked = false;
          if (underinsured_motorist_stacked) {
            updatedValues.underinsured_motorist_stacked = false;
          }
          sideEffectContext.push({
            type: 'UMST_UPDATE',
            name: coverageUpdateMap.UMST_UPDATE,
            isIncreased: false,
            cause: currentlyTargetedKeyName,
            causeName: formatCauseName(currentlyTargetedKeyName),
            isRemoved: true,
          });
        }
      }

      break;

    case 'underinsured_motorist_bodily_injury_limit':
      if (regionCode === 'SC') {
        if (
          coverageOptionStrToNum(
            underinsured_motorist_property_damage_limit || null
          ) === 0 &&
          coverageOptionStrToNum(underinsured_motorist_bodily_injury_limit) !==
            0
        ) {
          updatedValues.underinsured_motorist_property_damage_limit = '25000';
          sideEffectContext.push({
            type: 'UIMPD_UPDATE',
            name: coverageUpdateMap.UIMPD_UPDATE,
            isIncreased: true,
            isRemoved: false,
            cause: currentlyTargetedKeyName,
            causeName: formatCauseName(currentlyTargetedKeyName),
          });
        } else if (
          coverageOptionStrToNum(underinsured_motorist_bodily_injury_limit) ===
          0
        ) {
          updatedValues.underinsured_motorist_property_damage_limit = 'null';
          sideEffectContext.push({
            type: 'UIMPD_UPDATE',
            name: coverageUpdateMap.UIMPD_UPDATE,
            isIncreased: false,
            isRemoved: true,
            cause: currentlyTargetedKeyName,
            causeName: formatCauseName(currentlyTargetedKeyName),
          });
        }
      }

      const regionSpecificValue =
        regionSpecificUMBIUIMBICoverageMap?.[
          underinsured_motorist_bodily_injury_limit || ''
        ];

      const underinsuredBodilyInjuryLimitCompareValue = coverageOptionStrToNum(
        typeof regionSpecificValue === 'string'
          ? regionSpecificValue
          : underinsured_motorist_bodily_injury_limit
      );

      if (
        underinsuredBodilyInjuryLimitCompareValue >
        coverageOptionStrToNum(bodily_injury_limit)
      ) {
        if (!hasUimbiException) {
          updatedValues.bodily_injury_limit =
            underinsured_motorist_bodily_injury_limit;
          sideEffectContext.push({
            type: 'BODILY_INJURY_UPDATE',
            name: coverageUpdateMap.BODILY_INJURY_UPDATE,
            isIncreased: true,
            cause: currentlyTargetedKeyName,
            causeName: formatCauseName(currentlyTargetedKeyName),
          });
        }
      }

      if (UMBI_MATCHES_UIMBI.includes(regionCode)) {
        sideEffectContext.push({
          type: 'UMBI_UPDATE',
          name: coverageUpdateMap.UMBI_UPDATE,
          isIncreased:
            coverageOptionStrToNum(underinsured_motorist_bodily_injury_limit) >
            coverageOptionStrToNum(uninsured_motorist_bodily_injury_limit),
          cause: currentlyTargetedKeyName,
          causeName: formatCauseName(currentlyTargetedKeyName),
        });
        updatedValues.uninsured_motorist_bodily_injury_limit =
          regionSpecificUMBIUIMBICoverageMap?.[
            underinsured_motorist_bodily_injury_limit || ''
          ] || underinsured_motorist_bodily_injury_limit;
        if (
          UMPD_REQUIRES_UMBI.includes(regionCode) &&
          !!vehicles &&
          (underinsured_motorist_bodily_injury_limit === null ||
            underinsured_motorist_bodily_injury_limit === 'null')
        ) {
          updatedValues.vehicles = vehiclesValues?.map((vehicle) => {
            if (vehicle.values.uninsured_motorist_property_damage_limit) {
              sideEffectContext.push({
                type: 'VEHICLE_UMPD_UPDATE',
                name: coverageUpdateMap.UMPD_UPDATE,
                isIncreased: false,
                isRemoved: true,
                cause: currentlyTargetedKeyName,
                causeName: formatCauseName(currentlyTargetedKeyName),
              });
              return {
                ...vehicle,
                values: {
                  ...vehicle.values,
                  uninsured_motorist_property_damage_limit: null,
                },
              } as IVehicleCoverageValues;
            }
            return vehicle;
          });
        }
      } else if (
        !hasUimbiException &&
        underinsuredBodilyInjuryLimitCompareValue >
          coverageOptionStrToNum(uninsured_motorist_bodily_injury_limit)
      ) {
        updatedValues.uninsured_motorist_bodily_injury_limit =
          regionSpecificUMBIUIMBICoverageMap?.[
            underinsured_motorist_bodily_injury_limit || ''
          ] || underinsured_motorist_bodily_injury_limit;
        sideEffectContext.push({
          type: 'UMBI_UPDATE',
          name: coverageUpdateMap.UMBI_UPDATE,
          isIncreased: true,
          cause: currentlyTargetedKeyName,
          causeName: formatCauseName(currentlyTargetedKeyName),
        });
      }

      if (HIDE_UIMST_IF_UIMBI_NOT_INCLUDED.includes(regionCode)) {
        const isNotIncluded = [null, 'null', undefined];
        if (
          isNotIncluded.includes(underinsured_motorist_bodily_injury_limit) &&
          underinsured_motorist_stacked
        ) {
          updatedValues.underinsured_motorist_stacked = false;
          sideEffectContext.push({
            type: 'UIMST_UPDATE',
            name: coverageUpdateMap.UIMST_UPDATE,
            isIncreased: false,
            cause: currentlyTargetedKeyName,
            causeName: formatCauseName(currentlyTargetedKeyName),
            isRemoved: true,
          });
        }
      }

      break;

    default:
      break;
  }

  if (
    !!currentlyTargetedKeyName &&
    currentlyTargetedKeyName.startsWith('vehicles')
  ) {
    // TODO fix typings and get fn call in 154798
    const [vehicleIndex, _, vehicleCoverageKeyName]: string[] =
      currentlyTargetedKeyName.split('.');

    const vehicleValues = get(values, `${vehicleIndex}.values`);

    const callback = ({
      updateType,
      sideEffectContext: _sideEffectContext,
    }: {
      updateType: TCoverageUpdateType;
      sideEffectContext?: TCoverageSideEffectsType;
    }) => {
      typesOfUpdates.push(updateType);
      !!_sideEffectContext && sideEffectContext.push(_sideEffectContext);
    };

    const setFieldValue = (field: string, value: string) => {
      set(updatedValues, `${vehicleIndex}.values.${field}`, value);
    };

    const setPolicyValue = (field: string, value: string) => {
      set(updatedValues, field, value);
    };

    const _vehicleIndex = vehicleIndex?.split('[')?.[1].split(']')?.[0];

    vehicleCoverageOnChangeWrapper({
      formValues: vehicleValues,
      setPolicyValue,
      setValue: setFieldValue,
      bodilyInjuryCoverageValue: coverageOptionStrToNum(bodily_injury_limit),
      uninsuredMotoristPropertyDamageDeductibleValue: coverageOptionStrToNum(
        uninsured_motorist_property_damage_deductible
      ),
      callback,
      vehicleIndex: Number(_vehicleIndex),
      enabledSideEffects,
      regionCode,
      vehicles,
      policyEffectiveDate,
    })(
      vehicleCoverageKeyName as TCoverageValues,
      vehicleValues[vehicleCoverageKeyName]
    );
  }

  const newUpdates: TCoverageUpdateType[] = sideEffectContext.map(
    (effect) => effect?.type
  );

  typesOfUpdates.push(...newUpdates);

  return {
    updatedValues,
    typesOfUpdates,
    sideEffectContext,
  };
}
